import Grid from '@material-ui/core/Grid'
import React, { FC } from 'react'
import Button from '../button'

//* styles
import {
  HeroEyebrow,
  HeroEyebrowContainer,
  HeroInner,
  HeroSubheadline,
  HeroTitle,
  SolutionsHeroCircle,
} from './style'

//* utils
import { ASSETS } from '../../../constants/assets'
import { HERO_USECASES, PAGE_TYPES } from '../../../constants/useCases.constants'
import { TopicCTA } from '../../../models/topics/cta'
import ImgUtil from '../../../utils/img.utils'
import { internalLinkHandler } from '../../../utils/link.utils'
import { renderRichText } from '../../../utils/richTextHandler.utils'
import formatInlineCode from '../../../utils/shortTextFormatting'
import WrapperImage from '../wrapperImage'

type Props = {
  heroData: any
  pageUseCase: string
  imageSrc: string
  altText: string
  isHomePage: boolean
  pageType: string
  metaInformation: any
}

export const HeroGrid: FC<Props> = ({
  heroData,
  pageUseCase,
  imageSrc,
  altText,
  isHomePage,
  pageType,
  metaInformation,
}) => {
  let partnerPageHeroLogoUrl = ''
  let partnerLogoAltText = ''

  if (pageType === PAGE_TYPES.PARTNERS && metaInformation) {
    partnerPageHeroLogoUrl = metaInformation[0]?.colorLogo?.image?.file?.url || ''
    partnerLogoAltText = metaInformation[0]?.colorLogo?.altText || ''
  }

  const firstCta: TopicCTA = heroData.cta[0]
  const firstCtaLink = firstCta?.internalLink
    ? internalLinkHandler(firstCta.internalLink)
    : firstCta?.externalUrl || ''

  const secondCta: TopicCTA = heroData.cta[1]
  const secondCtaLink = secondCta?.internalLink
    ? internalLinkHandler(secondCta.internalLink)
    : secondCta?.externalUrl || ''

  const shouldDisplayDottedBackground =
    heroData.useCase &&
    pageType === PAGE_TYPES.SOLUTIONS &&
    heroData.useCase !== HERO_USECASES.MINIMAL &&
    heroData.useCase !== HERO_USECASES.FULL_WIDTH_HERO

  const shouldDisplayPartnersImage =
    pageUseCase === 'Business Partner Detail' &&
    heroData.useCase === HERO_USECASES.CENTERED_WITH_DOTS &&
    partnerPageHeroLogoUrl

  return (
    <HeroInner className="heroInner">
      {heroData.useCase === HERO_USECASES.FULL_WIDTH_HERO && pageType === 'solutions' ? (
        <div className="Hero__Solution-Mobile">
          <img src={imageSrc} alt={altText} />
        </div>
      ) : (
        ''
      )}

      <Grid
        container
        spacing={3}
        className={
          heroData?.useCase === HERO_USECASES.FULL_WIDTH_HERO ? 'Hero__FullWidth-CBlock' : ''
        }>
        <Grid
          item
          xs={12}
          sm={isHomePage ? 12 : 8}
          md={isHomePage ? 12 : heroData?.useCase === HERO_USECASES.FULL_WIDTH_HERO ? 6 : 8}
          className="Hero__Grid">
          <div>
            {!heroData?.logo && heroData.eyebrow && (
              <HeroEyebrowContainer>
                <HeroEyebrow type="caption">{heroData.eyebrow}</HeroEyebrow>
              </HeroEyebrowContainer>
            )}

            {shouldDisplayPartnersImage && (
              <img
                src={ImgUtil.getResizedImageUrl(partnerPageHeroLogoUrl, `width=150`)}
                alt={partnerLogoAltText}
              />
            )}
            {pageUseCase === 'Events' && heroData?.logo?.file.url && (
              <a href={firstCtaLink}>
                <WrapperImage className="hero__banner--logoimg" src={heroData?.logo?.file.url} />
              </a>
            )}
            {heroData.headline && (
              <HeroTitle type="header1">{formatInlineCode(heroData.headline)}</HeroTitle>
            )}

            {shouldDisplayDottedBackground && (
              <div className="Hero__Solutions-Dotted-Container">
                <div className="Hero__Solutions-Img-Circle-Robot">
                  <div className="Hero__Solutions-Img-Circle">
                    {imageSrc && (
                      <SolutionsHeroCircle
                        $backround={ImgUtil.getResizedImageUrl(
                          imageSrc,
                          `height=384`
                        )}></SolutionsHeroCircle>
                    )}
                  </div>

                  <img
                    src={ImgUtil.getResizedImageUrl(ASSETS.ROBOT_SPOTLIGHT, `width=150`)}
                    alt={altText}
                    className="Hero__Solutions-Img-Robot"
                  />
                </div>
              </div>
            )}
          </div>

          <div className="hero__content">
            {pageUseCase === 'Events' &&
            heroData.useCase === 'Product Hero' &&
            heroData?.subheadlineSmall ? (
              <HeroSubheadline type="lead">
                {renderRichText(heroData?.subheadlineSmall)}
              </HeroSubheadline>
            ) : (
              heroData?.subheadline && (
                <HeroSubheadline className="Lead" type="lead">
                  {formatInlineCode(heroData?.subheadline)}
                </HeroSubheadline>
              )
            )}

            {heroData.cta.length > 0 && (
              <div className="MultiBodyHeroHeader-CTAs">
                {firstCta && (
                  <Button
                    icon={true}
                    href={firstCtaLink}
                    text={firstCta.text ? firstCta.text : ''}
                    type={isHomePage && heroData.theme === 'dark' ? 'secondary' : 'primary'}
                    size={isHomePage ? 'medium' : 'small'}
                    theme={isHomePage && heroData.theme === 'dark' ? 'light' : heroData.theme}
                  />
                )}

                {secondCta && (
                  <Button
                    icon={true}
                    href={secondCtaLink}
                    text={secondCta.text ? secondCta.text : ''}
                    type={
                      heroData?.useCase === HERO_USECASES.FULL_WIDTH_HERO ? 'text-link' : 'tertiary'
                    }
                    className={`Hero__Secondary-CTA ${
                      heroData?.useCase === HERO_USECASES.FULL_WIDTH_HERO
                        ? heroData.theme === 'dark'
                          ? 'text-light'
                          : 'text-dark'
                        : ''
                    }`}
                    size={isHomePage ? 'medium' : 'small'}
                    theme={heroData.theme}
                  />
                )}
              </div>
            )}
          </div>
        </Grid>
      </Grid>
    </HeroInner>
  )
}
